import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import keyboardArrowDown from "../../Assets/keyboard_arrow_down.svg";
import pointIcon from "../../Assets/point_icon.svg";
import cardPlaceholder from "../../Assets/IDFC UAT - Credit card default image.png";
import { callActiveCardPostApi } from "../../Context/activeCardApi";
import { onSavePoints } from "../../Store/Slices/cardDataSlice";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import {
  CardBasedRedemptionList,
  LoginV3Data,
  useClientConfigFlag,
} from "../../Components/Utility/Utility";
const getIdentifierType = (card) => {
  try {
    return card?.identifiertype ? JSON.parse(card.identifiertype) : 1002;
  } catch {
    return 1002;
  }
};

// const getCardImage = (card) => {
//   const imageLink = card?.imagelink || card?.imageLink;
//   if (!imageLink) return null;
//   if (/^(https?:)?\/\//i.test(imageLink) || String(imageLink).startsWith("data:")) {
//     return String(imageLink).replace(/ /g, "%20");
//   }
//   return `${process.env.REACT_APP_CLIENT_URL}${imageLink}`.replace(/ /g, "%20");
// };

const getCardImage = (card) => {
  const imageLink = card?.backgroundcolor || card?.backgroundcolor;
  if (!imageLink) return null;
  if (/^(https?:)?\/\//i.test(imageLink) || String(imageLink).startsWith("data:")) {
    return String(imageLink).replace(/ /g, "%20");
  }
  return `${process.env.REACT_APP_CLIENT_URL}${imageLink}`.replace(/ /g, "%20");
};

const CardArtwork = ({ card, index = 0, compact = false }) => {
  const source = getCardImage(card);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [source]);

  return (
    // <span
    //   className={`card-balance-header__artwork${index % 2 === 1 ? " card-balance-header__artwork--maroon" : ""}${compact ? " card-balance-header__artwork--compact" : ""}`}
    //   aria-hidden="true"
    // >
    <span
      className={`card-balance-header__artwork${compact ? " card-balance-header__artwork--compact" : ""}`}
      aria-hidden="true"
    >
      {source && !hasImageError ? (
        <img
          src={source}
          alt=""
          loading={compact ? undefined : "lazy"}
          decoding="async"
          onError={() => setHasImageError(true)}
        />
      ) : (
        // <span>{card?.CardTypeName || "CC"}</span>
        <img
          src={cardPlaceholder}
          alt=""
          loading={compact ? undefined : "lazy"}
          decoding="async"
          onError={() => setHasImageError(true)}
        />
      )}
    </span>
  );
};

const CardBalanceHeader = () => {
  const dispatch = useDispatch();
  const translation = getTranslationData();
  const isCardHeaderDisplay = useClientConfigFlag("isCardHeaderDisplay");
  const loginV3Data = LoginV3Data();
  const isLoggedIn = sessionStorage.getItem("login") === "true";
  const cardList = CardBasedRedemptionList();
  const cardMasterData = useSelector(
    (state) => state.cardMasterReducer?.cardMasterData || [],
  );
  const activeCardIdentifier = useSelector(
    (state) => state.cardDataSliceReducer?.subIdentifier,
  );
  const activeCardPointBalance = useSelector(
    (state) => state.cardDataSliceReducer?.pointBalance,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => (
    window.matchMedia("(min-width: 1201px)").matches
  ));
  const [cardBalances, setCardBalances] = useState({});
  const headerRef = useRef(null);
  const inFlightRef = useRef(new Set());
  const attemptedRef = useRef(new Set());

  const cards = useMemo(() => {
    const availableCards = cardList?.[0]?.valueDetails || [];
    return availableCards.filter((card) => {
      const matchedCard = cardMasterData.find(
        (item) => item?.cardName === card?.CardTypeName,
      );
      const visibility = String(matchedCard?.isCustomerVisible ?? "true")
        .trim()
        .toLowerCase();
      return card?.status !== "0" && !["false", "0", "no"].includes(visibility);
    });
  }, [cardList, cardMasterData]);

  const selectedCardId =
    activeCardIdentifier
    || localStorage.getItem("selectedCardId")
    || sessionStorage.getItem("selectedCardId")
    || loginV3Data?.subIdentifier;
  const selectedCard = cards.find(
    (card) => String(card?.identifier) === String(selectedCardId),
  ) || cards[0];
  const selectedCardIndex = cards.findIndex(
    (card) => String(card?.identifier) === String(selectedCard?.identifier),
  );

  const fetchCardBalance = useCallback(async (card) => {
    const identifier = card?.identifier;
    if (!identifier || inFlightRef.current.has(identifier)) return;

    inFlightRef.current.add(identifier);
    attemptedRef.current.add(identifier);
    setCardBalances((previous) => ({
      ...previous,
      [identifier]: { loading: true, failed: false, points: null },
    }));

    try {
      const data = await callActiveCardPostApi({
        identifier: loginV3Data?.memberIdentifier,
        subIdentifier: identifier,
        identifierType: getIdentifierType(card),
        isAccountSwitch: false,
        userSessionToken: sessionStorage.getItem("loginSessionToken"),
      });

      if (String(data?.errorCode) === "200") {
        const points = Math.floor(
          data?.response?.response?.memberBalance?.pointBalance ?? 0,
        );
        setCardBalances((previous) => ({
          ...previous,
          [identifier]: { loading: false, failed: false, points },
        }));
        if (String(identifier) === String(selectedCard?.identifier)) {
          dispatch(onSavePoints({
            pointBalance: points,
            identifierType: card?.identifiertype,
            subIdentifier: identifier,
          }));
        }
      } else {
        setCardBalances((previous) => ({
          ...previous,
          [identifier]: { loading: false, failed: true, points: null },
        }));
      }
    } catch {
      setCardBalances((previous) => ({
        ...previous,
        [identifier]: { loading: false, failed: true, points: null },
      }));
    } finally {
      inFlightRef.current.delete(identifier);
    }
  }, [dispatch, loginV3Data?.memberIdentifier, selectedCard?.identifier]);

  useEffect(() => {
    const desktopMedia = window.matchMedia("(min-width: 1201px)");
    const handleDesktopChange = (event) => setIsDesktop(event.matches);
    desktopMedia.addEventListener("change", handleDesktopChange);
    return () => desktopMedia.removeEventListener("change", handleDesktopChange);
  }, []);

  useEffect(() => {
    attemptedRef.current.clear();
    inFlightRef.current.clear();
    setCardBalances({});
  }, [loginV3Data?.memberIdentifier]);

  useEffect(() => {
    if (
      activeCardIdentifier === undefined
      || activeCardIdentifier === null
      || activeCardPointBalance === undefined
      || activeCardPointBalance === null
    ) return;

    const nextBalance = Number(activeCardPointBalance);
    if (!Number.isFinite(nextBalance)) return;

    const identifier = String(activeCardIdentifier);
    setCardBalances((previous) => {
      const currentBalance = previous?.[identifier];
      const roundedBalance = Math.round(nextBalance);
      if (
        currentBalance?.loading === false
        && currentBalance?.failed === false
        && currentBalance?.points === roundedBalance
      ) return previous;

      return {
        ...previous,
        [identifier]: {
          loading: false,
          failed: false,
          points: roundedBalance,
        },
      };
    });
  }, [activeCardIdentifier, activeCardPointBalance]);

  useEffect(() => {
    if (!isLoggedIn || !isCardHeaderDisplay) return;
    cards.forEach((card) => {
      if (!attemptedRef.current.has(card?.identifier)) fetchCardBalance(card);
    });
  }, [cards, fetchCardBalance, isCardHeaderDisplay, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !isCardHeaderDisplay || !cards.length) setIsOpen(false);
  }, [cards.length, isCardHeaderDisplay, isLoggedIn]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    if (!isDesktop) document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      if (!isDesktop) document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDesktop, isOpen]);

  useEffect(() => {
    if (!isOpen || !isDesktop) return undefined;

    const closeOnOutsidePointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
    };
  }, [isDesktop, isOpen]);

  const totalPoints = cards.reduce((total, card) => {
    const points = cardBalances?.[card?.identifier]?.points;
    return total + (Number.isFinite(points) ? points : 0);
  }, 0);
  if (!isLoggedIn || !isCardHeaderDisplay || !selectedCard || !cards.length) return null;

  const renderPoints = (card, compact = false) => {
    const state = cardBalances?.[card?.identifier] || {};
    if (state.loading || !attemptedRef.current.has(card?.identifier)) {
      return <span className="card-balance-header__loading" aria-label="Loading points" />;
    }
    if (state.points != null) {
      return Number(state.points).toLocaleString("en-IN");
    }
    return (
      <button
        type="button"
        className={`card-balance-header__view-points${compact ? " card-balance-header__view-points--compact" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          fetchCardBalance(card);
        }}
      >
        {translation.cardHeaderViewPoints}
      </button>
    );
  };

  const desktopPortalTarget = isDesktop
    ? document.getElementById("desktop-card-balance-root")
    : null;
  const cardHeader = (
    <div ref={headerRef} className={`card-balance-header${desktopPortalTarget ? " card-balance-header--desktop" : ""}`}>
      <div className="card-balance-header__bar">
        <span className="card-balance-header__identity">
          <CardArtwork card={selectedCard} index={selectedCardIndex} compact />
          <span className="card-balance-header__card-label">
            <span className="card-balance-header__card-name">{selectedCard?.LogoDescription}</span>
            <span className="card-balance-header__card-number">•• {selectedCard?.CardNoLast4Digit}</span>
          </span>
        </span>
        <span className="card-balance-header__selected-points">
          <img src={pointIcon} alt="" aria-hidden="true" />
          {renderPoints(selectedCard, true)}
        </span>
        <button
          type="button"
          className="card-balance-header__toggle"
          aria-label="View card points"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((previous) => !previous)}
        >
          <img src={keyboardArrowDown} alt="" />
        </button>
      </div>

      {isOpen && (
        <div className="card-balance-header__overlay" role="presentation">
          <button
            type="button"
            className="card-balance-header__scrim"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setIsOpen(false)}
          />
          <section
            className={`card-balance-header__sheet${desktopPortalTarget ? " card-balance-header__dropdown" : ""}`}
            role="dialog"
            aria-modal={desktopPortalTarget ? undefined : "true"}
            aria-label="Card points"
          >
            <div className="card-balance-header__total">
              <strong>{translation.cardHeaderTotalPoints}</strong>
              <span>
                <img src={pointIcon} alt="" aria-hidden="true" />
                <b>{totalPoints.toLocaleString("en-IN")}</b>
              </span>
            </div>

            <div className="card-balance-header__list">
              {cards.map((card, index) => (
                <article className="card-balance-header__card" key={card.identifier}>
                  <CardArtwork card={card} index={index} />
                  <div className="card-balance-header__card-details">
                    <strong className="card-balance-header__card-label">
                      <span className="card-balance-header__card-name">{card?.LogoDescription}</span>
                      <span className="card-balance-header__card-number">•• {card?.CardNoLast4Digit}</span>
                    </strong>
                    <span className="card-balance-header__points">
                      <img src={pointIcon} alt="" aria-hidden="true" />
                      {renderPoints(card)}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            <p className="card-balance-header__note">
              {translation.cardHeaderReserveNote}
            </p>
          </section>
        </div>
      )}
    </div>
  );

  return desktopPortalTarget
    ? createPortal(cardHeader, desktopPortalTarget)
    : cardHeader;
};

export default CardBalanceHeader;
